"use strict";

import {
    BAD_REQUEST,
    FORBIDDEN,
    NOT_FOUND,
    UNPROCESSABLE_ENTITY,
} from "./common/common/common.constants";
import mixpanel from "mixpanel-browser";
import {
    addBreadcrumb,
    captureMessage,
    captureException,
} from "@sentry/browser";
import _ from "lodash";
import { cookiesRequiredState } from "./base.routes";
import { homescreenState } from "./base.routes";
import { growerSetupState } from "./grower.routes";
import { RejectType } from "@uirouter/core/lib-esm/transition/rejectFactory";

const GROWER_ROOT_STATE = "app.growers.grower";
const GROWER_SETTINGS_STATE = "app.growers.grower.settings";
const GROWER_PAST_DUE_STATE = "app.growers.grower.pastDueBilling";
const GROWER_MANAGEMENT_STATE = "app.growers.grower.management";
const GROWER_HELP_STATE = "app.growers.grower.help";
const GROWER_SETUP_STATE = growerSetupState.name;
const BILLING_REDIRECT_DAYS_THRESHOLD = 60;

function isImmediateGrowerChildState(stateName) {
    return _.startsWith(stateName, GROWER_ROOT_STATE + ".");
}

function isStateOrDescendant(stateName, parentStateName) {
    return (
        stateName === parentStateName ||
        _.startsWith(stateName, parentStateName + ".")
    );
}

/* @ngInject */
export default function setup(
    $urlRouter,
    $location,
    $anchorScroll,
    AccountService,
    $q,
    AuthService,
    OneSignalService,
    authManager,
    ToastService,
    ManagementMembershipService,
    GrowerManagementMembershipService,
    $transitions,
    localStorageService,
    $state,
    $urlService,
    $document,
    $rootScope,
    $window,
    GrowerMembershipService,
) {
    if (
        !localStorageService.isSupported ||
        !localStorageService.cookie.isSupported
    ) {
        $state.go(cookiesRequiredState);
    }
    let hijacked = $location.search().hijacked;
    if (hijacked) {
        $location.search("hijacked", null);
        localStorageService.set("hijacked", true);
    }
    AuthService.start()
        .then(
            function (token) {
                AuthService.setRefreshToken(token);
                AuthService.setLoggedInFlag();
                return AuthService.getAuthenticatedUserSid().then(
                    function (userSid) {
                        AccountService.retrieve(userSid).then(
                            function (account) {
                                account.updateSentry();
                                account.updateMixpanel();
                                account.updateLanguage();
                                account.updateIterate();
                            },
                        );
                        OneSignalService.login(userSid);

                        let growerManagementsResponse =
                            GrowerManagementMembershipService.list(
                                {
                                    user__sid: userSid,
                                    active: true,
                                },
                                {
                                    expand: ["permissions"],
                                },
                            );

                        let growerMembershipsResponse =
                            GrowerMembershipService.list({
                                user__sid: userSid,
                            });

                        return $q
                            .all([
                                growerManagementsResponse,
                                growerMembershipsResponse,
                            ])
                            .then(function (responses) {
                                return AccountService.updatePermissions(
                                    ...responses,
                                );
                            });
                    },
                    function () {
                        addBreadcrumb({
                            category: "auth",
                            message: "Could not get user sid from token",
                        });
                        captureMessage(
                            "Logging out due to unable to get user sid from token",
                        );
                        return AuthService.logout();
                    },
                );
            },
            function (reason) {
                addBreadcrumb({
                    category: "auth",
                    message: reason,
                });
                return AuthService.logout();
            },
        )
        .finally(function () {
            $urlRouter.sync();
            $urlRouter.listen();
        });

    $transitions.onEnter({}, function (transition) {
        const toState = transition.to();
        if (toState.external) {
            transition
                .injector()
                .getAsync("redirect")
                .then(function (redirect) {
                    $window.open(redirect, "_self");
                });
            return false;
        } else {
            addBreadcrumb({
                category: "navigation",
                message: "Transition started to " + toState.name,
            });
        }
        return true;
    });

    $transitions.onBefore({}, function (transition) {
        let toStateName = transition.to().name;
        if (
            !isImmediateGrowerChildState(toStateName) ||
            isStateOrDescendant(toStateName, GROWER_SETTINGS_STATE) ||
            isStateOrDescendant(toStateName, GROWER_MANAGEMENT_STATE) ||
            isStateOrDescendant(toStateName, GROWER_HELP_STATE) ||
            toStateName === GROWER_PAST_DUE_STATE
        ) {
            return true;
        }

        let growerSid = transition.params().grower_sid;
        if (!growerSid) {
            return true;
        }

        return transition
            .injector()
            .getAsync("grower")
            .then(function (grower) {
                if (!grower.is_active) {
                    return transition.router.stateService.target(
                        GROWER_SETUP_STATE,
                        {
                            grower_sid: growerSid,
                        },
                    );
                }

                let daysPastDue = Number(
                    grower.days_past_due_override ?? grower.days_past_due,
                );
                if (!(daysPastDue > BILLING_REDIRECT_DAYS_THRESHOLD)) {
                    return true;
                }

                return transition.router.stateService.target(
                    GROWER_PAST_DUE_STATE,
                    {
                        grower_sid: growerSid,
                    },
                );
            });
    });

    $transitions.onFinish({}, function (transition) {
        $rootScope.previousTransition = transition;
    });

    $transitions.onSuccess({}, function (transition) {
        let state = transition.to();
        addBreadcrumb({
            category: "navigation",
            message: "Transition finshed to " + state.name,
        });
        let mixpanelEvent = _.get(state, "data.mixpanelEvent");
        if (mixpanelEvent) {
            let mixpanelEventProperties = _.get(
                state,
                "data.mixpanelEventProperties",
                {},
            );
            let resolvedMixpanelProperties = {};
            _.forEach(mixpanelEventProperties, function (resolvePath, name) {
                let [obj, path] = resolvePath.split(".", 2);
                let resolvedObject = transition.injector().get(obj);
                resolvedMixpanelProperties[name] = _.get(resolvedObject, path);
            });
            if (isImmediateGrowerChildState(state.name)) {
                resolvedMixpanelProperties.company_sid =
                    transition.params().grower_sid;
            }
            mixpanel.track(mixpanelEvent, resolvedMixpanelProperties);
        }

        let elementID = transition.params().scrollTo;
        if (elementID) {
            $location.hash(elementID);
            $anchorScroll(elementID);
        }

        $rootScope.showSidebar = false;
    });

    $transitions.onError({}, (transition) => {
        const err = transition.error();
        const type = err?.type;
        const info = err?.detail || {};

        // --- 1) Code-split failure ---------------------------------------------
        if (info.name === "ChunkLoadError") {
            captureException("Handling ChunkLoadError", {
                contexts: {
                    error: {
                        from_state: transition.from().name,
                        to_state: transition.to().name,
                        status: info.status,
                        data: JSON.stringify(info.data),
                    },
                },
            });
            $window.location.reload(true);
            return;
        }

        if (type === RejectType.SUPERSEDED) {
            return;
        }

        // Only act on real routing errors
        if (type !== RejectType.INVALID && type !== RejectType.ERROR) {
            return;
        }

        // --- 2) Server-driven redirect ------------------------------------------
        if (info.redirectTo) {
            return $state.go(info.redirectTo.state, info.redirectTo.params);
        }

        // --- 3) Expected API status codes ---------------------------------------
        if (
            [BAD_REQUEST, FORBIDDEN, NOT_FOUND, UNPROCESSABLE_ENTITY].includes(
                info.status,
            )
        ) {
            mixpanel.track("Transition error");
            captureException(`Transition Error: ${info.status}`, {
                contexts: {
                    error: {
                        from_state: transition.from().name,
                        to_state: transition.to().name,
                        status: info.status,
                        data: JSON.stringify(info.data),
                    },
                },
            });
            $state.go(homescreenState);
            return;
        }

        // --- 4) Anything else ----------------------------------------------------
        captureException(info, {
            contexts: {
                error: {
                    from_state: transition.from().name,
                    to_state: transition.to().name,
                },
            },
        });
        $state.go(homescreenState, {}, { reload: true });
    });

    $state.defaultErrorHandler(function () {
        // This is empty to prevent the default error handler from logging,
        // instead we're handling them in .onError method of the transition service
    });

    $window.addEventListener("storage", (event) => {
        if (event.key === "ls.refresh") {
            if (event.newValue !== null) {
                let oldUserSid = AuthService.getAuthenticatedUserSid(
                    event.oldValue,
                );
                let newUserSid = AuthService.getAuthenticatedUserSid(
                    event.newValue,
                );
                $q.all([oldUserSid, newUserSid]).then(function ([
                    oldSid,
                    newSid,
                ]) {
                    if (oldSid !== newSid) {
                        addBreadcrumb({
                            category: "auth",
                            message: "User was changed in another tab",
                            data: { oldSid, newSid },
                        });
                        $state.go(homescreenState, {}, { reload: true });
                    }
                });
            } else {
                AuthService.logout();
            }
        }
    });

    $rootScope.$on(
        "LocalStorageModule.notification.setitem",
        function (event, opts) {
            addBreadcrumb({
                category: "auth",
                message: "Localstorage set",
            });
        },
    );
    $rootScope.$on(
        "LocalStorageModule.notification.removeitem",
        function (event, opts) {
            addBreadcrumb({
                category: "auth",
                message: "Localstorage remove",
            });
        },
    );

    AuthService.getAuthenticatedUserSid().then(
        function (userSid) {
            mixpanel.identify(userSid);
            mixpanel.track("App initialized");
        },
        function () {},
    );
}
